<template>
  <transition name="fade">
    <div v-show="sortView.visible" class="doc-sort" @click.self="hide">
      <div class="doc-sort__inner">
        <ul class="doc-sort__list">
          <li
            v-for="opt in sortView.options"
            :key="opt.name"
            class="doc-sort__item"
            :class="sortView.activeOptionClass(opt)"
            @click="sortView.optionClick(opt)"
          >
            <span class="doc-sort__title"
              ><span class="doc-sort__icon iconfont" :class="opt.icon"></span
              >{{ opt.name }}</span
            >
          </li>
        </ul>
        <div class="doc-sort__btn-box">
          <a class="doc-sort__btn btns" @click="sortView.applySort()">Apply</a>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'

import { DocsView } from '@/models/doc'
import { DocsSortMobileView as DocsSortView } from '@/models/docs.sort'
import { assertNotNull } from '@/helpers/typing'
import { emitter } from '@/services/mitt'

const props = defineProps<{
  view: DocsView
}>()

const sortView = ref(new DocsSortView(props.view))

onBeforeMount(async () => {
  emitter.on('openSortPopup', () => {
    show()
  })
})

function hide(): void {
  assertNotNull(sortView.value).hide()
}

function show(): void {
  assertNotNull(sortView.value).show()
}
</script>

<style lang="scss" scoped>
.doc-sort {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 6;
  width: 100%;
  height: 100vh;
  background-color: rgba($color: $c-black-2, $alpha: 0.3);

  &__inner {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 3;
    width: 100%;
    border-radius: 14px 14px 0 0;
    padding: 48px 0 28px;
    background-color: $c-white;
    &:before {
      display: block;
      content: '';
      width: 80px;
      height: 4px;
      background-color: $c-border-2;
      position: absolute;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      border-radius: 100px;
    }
  }

  &__list {
    padding: 0 40px;
    @media only screen and (max-width: $w-medium-s) {
      padding: 0 24px;
    }
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    line-height: 20px;
    color: $c-black-3;
    border-bottom: 1px solid $c-border;
    padding-bottom: 16px;
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
      border: none;
      padding: 0;
    }
    &:after {
      display: inline-block;
      content: '';
      width: 24px;
      height: 24px;
      background-image: url('@/assets.app/images/check.svg');
      background-size: 100% 100%;
      position: relative;
      top: -1px;
      opacity: 0;
      transition: all 0.2s ease-in-out;
    }
    &--active {
      &:after {
        opacity: 1;
      }
    }
  }

  &__title {
    display: flex;
    align-items: center;
  }

  &__icon {
    font-size: 24px;
    margin-right: 8px;
    position: relative;
    top: -1px;
  }

  &__btn-box {
    margin-top: 28px;
    padding: 20px 13px 0;
    border-top: 1px solid $c-border-2;
    text-align: center;
  }

  &__btn {
    display: block;
    font-size: 18px;
    line-height: 22px;
    color: $c-black-2;
    text-align: center;
    background-color: $c-yellow;
    border-radius: 53px;
    padding: 14px 22px 12px;
    max-width: 327px;
    margin: 0 auto;
    @include hover {
      background-color: $c-yellow-5;
      color: $c-black-2;
    }
  }
}

.night-mode .doc-sort {
  background-color: rgba($color: $c-dark-1, $alpha: 0.6);

  &__inner {
    background-color: $c-dark-3;
    &:before {
      background-color: $c-dark-8;
    }
  }

  &__item {
    color: $c-white;
    border-color: $c-dark-5;
  }

  &__btn-box {
    border-color: $c-dark-5;
  }
}
</style>
